import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import MemoryForm from './MemoryForm';
import Dropdown from './Dropdown';
import { A, CAT_COLORS, inputStyle as iStyle, btnStyle as avgBtn, iconBtnStyle as ib } from './theme';

function MemoriesPanel({ api, refreshKey, onDataChange }) {
  const [memories, setMemories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [deleting, setDeleting] = useState(null);
  const searchRef = useRef(search);
  const catRef = useRef(catFilter);
  searchRef.current = search;
  catRef.current = catFilter;

  const loadAbortRef = useRef(null);
  const load = useCallback(async () => {
    if (loadAbortRef.current) loadAbortRef.current.abort();
    const controller = new AbortController();
    loadAbortRef.current = controller;
    setLoading(true);
    const p = new URLSearchParams();
    if (searchRef.current) p.set('keyword', searchRef.current);
    if (catRef.current) p.set('category', catRef.current);
    try { const r = await fetch(`${api}/dashboard/memories?${p}`, { signal: controller.signal }); const d = await r.json(); if (!controller.signal.aborted) { setMemories(d.data || []); setFetchError(false); } }
    catch (e) { if (e.name !== 'AbortError') { setMemories([]); setFetchError(true); } }
    if (!controller.signal.aborted) setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);
  // Cleanup abort controller on unmount
  useEffect(() => { return () => { if (loadAbortRef.current) loadAbortRef.current.abort(); }; }, []);
  useEffect(() => { if (refreshKey > 0) load(); }, [refreshKey, load]);
  // Re-fetch when category filter changes (dropdown selection is an intentional action)
  const prevCatRef = useRef(catFilter);
  useEffect(() => { if (prevCatRef.current !== catFilter) { prevCatRef.current = catFilter; load(); } }, [catFilter, load]);

  const onDataChangeRef = useRef(onDataChange);
  onDataChangeRef.current = onDataChange;

  const deleteMem = useCallback(async id => { try { const r = await fetch(`${api}/dashboard/memories/${id}`, { method: 'DELETE' }); if(r.ok) { setMemories(p => p.filter(m => m.id !== id)); if(onDataChangeRef.current) onDataChangeRef.current(); } } catch{} setDeleting(null); }, [api]);
  const handleEdit = useCallback(m => { setEditing(m); setShowForm(true); }, []);
  const onSaved = useCallback(() => { setShowForm(false); setEditing(null); load(); if(onDataChangeRef.current) onDataChangeRef.current(); }, [load]);

  // Build hierarchy: category → group → items (memoized)
  const hierarchy = useMemo(() => {
    const h = {};
    memories.forEach(m => {
      const cat = m.category || 'other';
      const grp = m.group || null;
      if (!h[cat]) h[cat] = { ungrouped: [], groups: {} };
      if (grp) { if (!h[cat].groups[grp]) h[cat].groups[grp] = []; h[cat].groups[grp].push(m); }
      else h[cat].ungrouped.push(m);
    });
    return h;
  }, [memories]);

  return (
    <div>
      {/* Search */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search database..."
          aria-label="Search memories" onKeyDown={e => e.key === 'Enter' && load()} style={{ ...iStyle, flex: 1, minWidth: '120px' }} />
        <Dropdown value={catFilter} onChange={setCatFilter} style={{width:'120px'}} aria-label="Filter by category"
          options={[{value:'',label:'All Sectors'},{value:'personal',label:'Personal'},{value:'contact',label:'Contact'},{value:'credentials',label:'Credentials'},{value:'academic',label:'Academic'},{value:'other',label:'Other'}]} />
        <button onClick={load} style={avgBtn}>Search</button>
      </div>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '14px', color: A.text, fontWeight: 600, letterSpacing: '1px', textTransform: 'uppercase' }}>Intelligence Database</h2>
          <p style={{ margin: '2px 0 0', fontSize: '10px', color: A.dim }}>{memories.length} record{memories.length !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }} style={{ ...avgBtn, background: `linear-gradient(135deg, ${A.primary}25, ${A.accent}10)`, border: `1px solid ${A.primary}30` }}>+ New Record</button>
      </div>

      {/* Hierarchical display */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: A.dim }}>
          <div style={{ display: 'inline-flex', gap: '4px' }}>
            {[0,1,2].map(i => <div key={i} style={{ width: '6px', height: '6px', borderRadius: '50%', background: A.primary, animation: `td 1.4s ease-in-out ${i*0.2}s infinite` }} />)}
          </div>
          <p style={{ marginTop: '8px', fontSize: '11px', letterSpacing: '1px' }}>ACCESSING DATABASE...</p>
        </div>
      ) : memories.length === 0 ? (
        <p style={{ color: A.dim, textAlign: 'center', padding: '40px', fontSize: '12px', letterSpacing: '1px' }}>{fetchError ? 'FAILED TO LOAD DATABASE. CHECK CONNECTION.' : 'DATABASE EMPTY. NO RECORDS FOUND.'}</p>
      ) : (
        Object.entries(hierarchy).map(([cat, { ungrouped, groups }]) => (
          <div key={cat} style={{ marginBottom: '16px', animation: 'fsu 0.3s ease-out' }}>
            {/* Category header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <div style={{
                width: '3px', height: '16px', borderRadius: '2px',
                background: CAT_COLORS[cat] || '#6b7280',
                boxShadow: `0 0 6px ${CAT_COLORS[cat] || '#6b7280'}50`,
              }} />
              <span style={{
                fontSize: '11px', fontWeight: 700, letterSpacing: '1.5px',
                color: CAT_COLORS[cat] || '#6b7280', textTransform: 'uppercase',
              }}>{cat}</span>
              <span style={{ fontSize: '10px', color: A.dim }}>
                {ungrouped.length + Object.values(groups).reduce((s, g) => s + g.length, 0)}
              </span>
            </div>

            {/* Grouped items */}
            {Object.entries(groups).map(([grpName, items]) => (
              <div key={grpName} style={{
                marginBottom: '8px', marginLeft: '12px',
                borderLeft: `2px solid ${CAT_COLORS[cat] || '#6b7280'}30`,
                paddingLeft: '12px',
              }}>
                <div style={{
                  fontSize: '12px', color: A.primary, fontWeight: 600, marginBottom: '5px',
                  letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '6px',
                }}>
                  <span style={{ fontSize: '8px', opacity: 0.5 }}>◆</span> {grpName}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                  {items.map(m => <MemCard key={m.id} mem={m} del={deleting} setDel={setDeleting} onDel={deleteMem} onEdit={handleEdit} />)}
                </div>
              </div>
            ))}

            {/* Ungrouped */}
            {ungrouped.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', marginLeft: '12px' }}>
                {ungrouped.map(m => <MemCard key={m.id} mem={m} del={deleting} setDel={setDeleting} onDel={deleteMem} onEdit={handleEdit} />)}
              </div>
            )}
          </div>
        ))
      )}

      {showForm && <MemoryForm api={api} memory={editing} onClose={() => { setShowForm(false); setEditing(null); }} onSaved={onSaved} />}
    </div>
  );
}

const MemCard = React.memo(function MemCard({ mem: m, del, setDel, onDel, onEdit }) {
  return (
    <div style={{
      padding: '8px 12px', borderRadius: '6px',
      background: A.surface, border: `1px solid ${A.border}`,
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      animation: 'fsu 0.2s ease-out',
    }}>
      <div style={{ minWidth: 0 }}>
        <span style={{ color: A.dim, fontSize: '10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>{m.key}: </span>
        <span style={{ color: A.text, fontSize: '13px', fontWeight: 500, overflowWrap: 'break-word' }}>{m.value}</span>
      </div>
      <div style={{ display: 'flex', gap: '2px', flexShrink: 0, marginLeft: '8px' }}>
        <button onClick={() => onEdit(m)} aria-label="Edit memory" style={ib}>✎</button>
        {del === m.id ? (
          <><button onClick={() => onDel(m.id)} aria-label="Confirm delete" style={{ ...ib, color: '#ef4444' }}>✓</button><button onClick={() => setDel(null)} aria-label="Cancel delete" style={ib}>✗</button></>
        ) : (
          <button onClick={() => setDel(m.id)} aria-label="Delete memory" style={{ ...ib, color: '#ef4444' }}>✗</button>
        )}
      </div>
    </div>
  );
});

/* Styles imported from theme.js */

export default MemoriesPanel;
